import {Injectable} from '@angular/core';
import {
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  QuerySnapshot,
  setDoc,
} from '@angular/fire/firestore';
import {path_users_userId_settings_stores_storeId_mailer_settings} from '../database-paths';
import {firstValueFrom, from, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {selectColleagues} from '../../../features/core/store-shared/shared.selectors';
import {Store} from '@ngrx/store';
import {killColleaguesService} from '../../../features/core/store-shared/shared.actions';
import {IColleagues, IUserAccess} from '../../../features/user-access/models';
import {
  selectUserAccess,
} from '../../../features/user-access/user-access-store/user-access.selectors';
import {IEmailSettings} from '../../../features/settings-new/models';
import {sanitizeMailerSettings} from '../../../features/settings-new/models/email/microsoft-mail.utils';
import {isStoreAdminForStore} from '../../shared-utils/user-access/user-access.utils';

export interface IMailerSettingsPresence {
  documentExists: Record<string, boolean>;
  settings: Record<string, IEmailSettings>;
}

@Injectable({
  providedIn: 'root',
})
export class CollectionUsersSettingsService {

  constructor(
    private readonly store: Store,
    private firestore: Firestore,
  ) {
  }

  // ====================================================================================================
  // Generic Get/Set/Delete
  // ====================================================================================================

  async setDocument<DataType>(
    path: string,
    data: DataType,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await setDoc(docRef, data, {merge: true});
    } catch (error) {
      console.error(error);
    }
  }

  async setDocumentNoMerge<DataType>(
    path: string,
    data: DataType,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await setDoc(docRef, data);
    } catch (error) {
      console.error(error);
    }
  }

  async getDocument<DataType>(
    path: string,
  ): Promise<DataType> {
    try {
      const docRef = doc(this.firestore, path);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        return docSnapshot.data() as DataType;
      } else {
        return {} as DataType;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async deleteDocument<DataType>(
    path: string,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document at path: ${path}\n`, error);
    }
  }

  async deleteDocumentAndSubCollections<DataType>(
    path: string,
    subCollections: string[],
  ): Promise<void> {
    try {
      const docRef: DocumentReference = doc(this.firestore, path);
      for (const subCollection of subCollections) {
        const subCollectionRef: CollectionReference = collection(docRef, subCollection);
        const subCollectionDocs: QuerySnapshot = await getDocs(subCollectionRef);
        for (const docSnap of subCollectionDocs.docs) {
          await deleteDoc(docSnap.ref);
        }
      }
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document and its sub collections at path: ${path}\n`, error);
      throw error;
    }
  }


  getAllMailerSettings(storeId: string): Observable<Record<string, IEmailSettings>> {
    return this.getAllMailerSettingsPresence(storeId).pipe(
      map((loaded: IMailerSettingsPresence): Record<string, IEmailSettings> => loaded.settings),
    );
  }

  /**
   * Loads each colleague's mailer settings and whether that Firestore document exists.
   * Existence comes from the snapshot, so an empty document is still present.
   */
  getAllMailerSettingsPresence(storeId: string): Observable<IMailerSettingsPresence> {
    return from(this.getAllMailerSettingsAsync(storeId));
  }

  private async getAllMailerSettingsAsync(storeId: string): Promise<IMailerSettingsPresence> {
    const userAccess: IUserAccess = await firstValueFrom(this.store.select(selectUserAccess));

    if (!isStoreAdminForStore(userAccess, storeId)) {
      return {settings: {}, documentExists: {}};
    }
    try {
      const allColleagues: IColleagues = await firstValueFrom(this.store.select(selectColleagues));
      if (!allColleagues || Object.keys(allColleagues).length === 0) {
        return {settings: {}, documentExists: {}};
      }

      const userIds = Object.keys(allColleagues).filter((userId: string) => {
        const colleague = allColleagues[userId];
        return colleague.storeList?.includes(storeId);
      });

      if (userIds.length === 0) {
        return {settings: {}, documentExists: {}};
      }

      const settingsPromises = userIds.map(async (userId: string) => {
        try {
          const docPath: string = path_users_userId_settings_stores_storeId_mailer_settings(userId, storeId);
          const docRef = doc(this.firestore, docPath);
          const snapshot = await getDoc(docRef);
          const raw = snapshot.exists() ? snapshot.data() : {};
          return {userId, settings: sanitizeMailerSettings(raw), exists: snapshot.exists()};
        } catch (error) {
          return {userId, settings: {} as IEmailSettings, exists: false};
        }
      });

      const results = await Promise.all(settingsPromises);

      const settings: Record<string, IEmailSettings> = {};
      const documentExists: Record<string, boolean> = {};
      results.forEach(({userId, settings: userSettings, exists}) => {
        settings[userId] = userSettings;
        documentExists[userId] = exists;
      });
      return {settings, documentExists};
    } catch (error) {
      this.store.dispatch(killColleaguesService());
      return {settings: {}, documentExists: {}};
    }
  }

}
